import React, { useCallback, useEffect, useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useCurrentCompany } from 'src/modules/companies/hooks';
import { useGetDefaultCustomFieldsApi } from 'src/api/customFields';
import { GetDefaultCustomFieldsParams } from 'src/api/customFields/types';
import { useGetMessageTemplatesApi } from 'src/api/messageTemplates';
import { GetMessageTemplatesParams } from 'src/api/messageTemplates/types';
import { DefaultCustomField, MessageTemplate } from 'src/constants/types';
import { useCampaignsApi } from 'src/modules/campaigns/api';
import { useUserCompaniesService } from 'src/modules/companies/services';
import {
  getCurrentCompanyId,
  getCurrentCompanyAvailabilityStatus,
} from 'src/modules/companies/slices';
import {
  getAwayCustomMessageCampaign as loadAwayCustomMessageCampaign,
  getAwayRecurringMessagesCampaign as loadAwayRecurringMessagesCampaign,
} from 'src/modules/campaigns/slices';
import { useAwayMessageController } from 'src/modules/campaigns/controllers';
import { useCampaignService } from 'src/modules/campaigns/services';
import { AwayMessageDrawer } from '.';
import { NON_CAMPAIGN_TEMPLATE_VALUE_TYPES } from 'src/constants/templateValueTypes';

const AwayMessageSwitcher = () => {
  const selectedCompanyId = useSelector(getCurrentCompanyId);
  const availabilityStatus = useSelector(getCurrentCompanyAvailabilityStatus);
  const awayCustomMessageCampaign = useSelector(loadAwayCustomMessageCampaign);
  const awayRecurringMessagesCampaign = useSelector(loadAwayRecurringMessagesCampaign);
  const { getCompanies } = useUserCompaniesService();
  const { activateCampaign: activate, deactivateCampaign: deactivate } = useCampaignsApi();
  const { getAwayMessageCampaigns, loading: campaignsLoading } = useCampaignService();
  const { refetch: refetchCompany } = useCurrentCompany();

  const [metadata, setMetadata] = useState<DefaultCustomField[]>();
  const [messageTemplates, setMessageTemplates] = useState<MessageTemplate[]>();
  const [loading, setLoading] = useState<boolean>(false);

  const getMetadata = useGetDefaultCustomFieldsApi();
  const getMessageTemplates = useGetMessageTemplatesApi();

  // Lazy-load drawer data (default values + message templates) only when the
  // drawer is opened. Cached in state after first load so repeat opens are free.
  const loadTemplateData = useCallback(async () => {
    if (!selectedCompanyId) {
      return { customFields: metadata || [], messageTemplates: messageTemplates || [] };
    }

    let fields = metadata;
    let templates = messageTemplates;

    if (!fields) {
      const params: GetDefaultCustomFieldsParams = {
        companyId: selectedCompanyId,
        types: NON_CAMPAIGN_TEMPLATE_VALUE_TYPES,
      };
      try {
        const data = await getMetadata(params);
        fields = data.data.items;
        setMetadata(fields);
      } catch (e) {
        console.log(e);
      }
    }

    if (!templates) {
      const messageTemplatesParams: GetMessageTemplatesParams = {
        companyId: selectedCompanyId,
        offset: 0,
        limit: 10000,
        status: 'active',
      };
      try {
        const data = await getMessageTemplates(messageTemplatesParams);
        templates = data.data.items.filter(t => t.companyId === selectedCompanyId);
        setMessageTemplates(templates);
      } catch (e) {
        console.log(e);
      }
    }

    return { customFields: fields || [], messageTemplates: templates || [] };
  }, [selectedCompanyId, metadata, messageTemplates, getMetadata, getMessageTemplates]);

  const handleStatusChange = async (event: SelectChangeEvent) => {
    if (!selectedCompanyId) {
      return;
    }

    const status = event.target.value;
    if (status === 'custom') {
      if (
        awayCustomMessageCampaign?.id &&
        awayCustomMessageCampaign.scheduledMessages.some(m => m.text)
      ) {
        setLoading(true);
        if (awayRecurringMessagesCampaign?.id) {
          await deactivate(selectedCompanyId, [awayRecurringMessagesCampaign.id]);
        }

        activate(selectedCompanyId, [awayCustomMessageCampaign.id]).then(async () => {
          await Promise.all([
            await getCompanies(),
            await refetchCompany(),
            await getAwayMessageCampaigns(selectedCompanyId),
          ]);
          setLoading(false);
        });
      } else {
        const drawerData = await loadTemplateData();
        awayMessageController.open(drawerData, status, true);
      }
    }

    if (status === 'recurring') {
      if (
        awayRecurringMessagesCampaign?.id &&
        awayRecurringMessagesCampaign.scheduledMessages.some(s => s.text)
      ) {
        setLoading(true);
        if (awayCustomMessageCampaign?.id) {
          await deactivate(selectedCompanyId, [awayCustomMessageCampaign.id]);
        }

        activate(selectedCompanyId, [awayRecurringMessagesCampaign.id]).then(async () => {
          await Promise.all([
            await getCompanies(),
            await refetchCompany(),
            await getAwayMessageCampaigns(selectedCompanyId),
          ]);
          setLoading(false);
        });
      } else {
        if (awayCustomMessageCampaign?.id) {
          await deactivate(selectedCompanyId, [awayCustomMessageCampaign.id]);
        }
        const drawerData = await loadTemplateData();
        awayMessageController.open(
          drawerData,
          status === 'recurring' ? 'recurring' : 'custom',
          true,
        );
      }
    }

    if (status === 'off') {
      const campaignIds = [];
      if (awayCustomMessageCampaign?.id) {
        campaignIds.push(awayCustomMessageCampaign.id);
      }
      if (awayRecurringMessagesCampaign?.id) {
        campaignIds.push(awayRecurringMessagesCampaign.id);
      }
      if (campaignIds && campaignIds.length) {
        setLoading(true);
        deactivate(selectedCompanyId, campaignIds).then(async () => {
          await Promise.all([
            await getCompanies(),
            await refetchCompany(),
            await getAwayMessageCampaigns(selectedCompanyId),
          ]);
          setLoading(false);
        });
      }
    }
  };

  const awayMessageController = useAwayMessageController();

  useEffect(() => {
    // Drop cached drawer data so a company switch re-fetches on next open.
    setMetadata(undefined);
    setMessageTemplates(undefined);
    if (selectedCompanyId) {
      getAwayMessageCampaigns(selectedCompanyId);
    }
  }, [selectedCompanyId]);

  const renderMenuItem = (label: string) => {
    return (
      <MenuItem value={label.toLowerCase()}>
        <Stack direction="row" alignItems="center">
          {availabilityStatus === 'no_active_plan' ? 'Off' : label}
          {(loading || campaignsLoading.getAwayMessageCampaigns) && (
            <CircularProgress size="10px" sx={{ marginLeft: '5px' }} />
          )}
        </Stack>
      </MenuItem>
    );
  };

  const status =
    awayCustomMessageCampaign?.status === 'active'
      ? 'custom'
      : awayRecurringMessagesCampaign?.status === 'active'
      ? 'recurring'
      : 'off';

  return (
    <Box
      sx={{
        display: 'flex',
        direction: 'row',
        width: '100%',
        justifyContent: 'flex-around',
        alignItems: 'center',
        marginTop: 0,
      }}
    >
      <AwayMessageDrawer {...awayMessageController} />
          <Box sx={{ display: 'flex', flex: 10, alignItems: 'center', visibility: awayCustomMessageCampaign && awayRecurringMessagesCampaign ? 'visible' : 'hidden' }}>
            <Typography sx={{ marginRight: '5px', marginTop: '5px' }}>Auto-reply: </Typography>
            <FormControl variant="standard" sx={{ minWidth: '57%' }}>
              <Select
                disabled={availabilityStatus === 'no_active_plan'}
                labelId="select-status-label"
                id="status-standard"
                value={status}
                onChange={handleStatusChange}
                label="Status"
                sx={{
                  '& .MuiInputBase-input': {
                    paddingBottom: 0,
                  },
                }}
              >
                {renderMenuItem('Off')}
                {renderMenuItem('Custom')}
                {renderMenuItem('Recurring')}
              </Select>
            </FormControl>
          </Box>
          <EditIcon
            htmlColor="grey"
            sx={{
              flex: 1,
              cursor: availabilityStatus === 'no_active_plan' ? 'initial' : 'pointer',
              visibility: awayCustomMessageCampaign && awayRecurringMessagesCampaign ? 'visible' : 'hidden',
            }}
            onClick={async () => {
              if (availabilityStatus !== 'no_active_plan') {
                const drawerData = await loadTemplateData();
                awayMessageController.open(
                  drawerData,
                  status === 'recurring' ? 'recurring' : 'custom',
                  false,
                );
              }
            }}
          />
    </Box>
  );
};

export default AwayMessageSwitcher;
